import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { ButtonColor } from "@/components/Button/types";
import { Spinner } from "@/components/Spinner/Spinner";
import { CompanySelection } from "@/components/CompanyBlacklistToggle/companyBlacklist";
import { CompanyBlacklistToggle } from "@/components/CompanyBlacklistToggle/CompanyBlacklistToggle";

import { useContactBlacklistImpactMutation } from "@/pages/Private/redux/contact/contact.api";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { ContactAddToBlacklistRequest, ContactBlacklistImpactResponse } from "../schema/contacts";

/** The companies the toggle names: unknown until the impact preview has counted them. */
const companySelectionOf = (
	impact: ContactBlacklistImpactResponse | undefined,
	isLoading: boolean,
	selectedCount: number
): CompanySelection =>
	impact && !isLoading
		? { companies: impact.companies, count: impact.companyCount, leadCount: selectedCount }
		: null;

interface BlacklistConfirmModalProps {
	isOpened: boolean;
	/** The same selection payload the add-to-blacklist call will use — drives the impact preview. */
	selectionPayload: ContactAddToBlacklistRequest;
	/** How many contacts the action targets, for the confirmation copy. */
	selectedCount: number;
	isConfirmLoading: boolean;
	addEntireCompany: boolean;
	onToggleEntireCompany: (value: boolean) => void;
	onClose: () => void;
	onConfirm: () => void | Promise<void>;
}

/**
 * Confirmation step before blacklisting a Contacts selection. On open it fetches the impact preview
 * (how many of the selected contacts are on lists / in an active campaign) so the user is warned —
 * blacklisting a lead that is mid-campaign halts that campaign — before the action commits.
 */
export const BlacklistConfirmModal = ({
	isOpened,
	selectionPayload,
	selectedCount,
	isConfirmLoading,
	addEntireCompany,
	onToggleEntireCompany,
	onClose,
	onConfirm,
}: BlacklistConfirmModalProps) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) => t(`contacts.${key}`, options);

	const [fetchImpact, { data: impact, isLoading: isImpactLoading, reset }] =
		useContactBlacklistImpactMutation();
	// A double click must not add the selection twice (PREC-4425).
	const confirm = useSingleFlight();

	// Payload identity changes each parent render; keep it in a ref so the fetch fires once per open
	// (not on every render) while still using the latest selection.
	const payloadRef = useRef(selectionPayload);

	payloadRef.current = selectionPayload;

	// `reset` is recreated after every request (RTK Query keys it on the request id), so it must not
	// be an effect dependency: each answer would re-run the effect and fire the next request, for as
	// long as the modal stays open (PREC-4206). Read the latest one through a ref instead.
	const resetRef = useRef(reset);

	resetRef.current = reset;

	useEffect(() => {
		if (!isOpened) {
			return;
		}

		fetchImpact(payloadRef.current);

		return () => resetRef.current();
	}, [isOpened, fetchImpact]);

	if (!isOpened) {
		return null;
	}

	return (
		<Modal
			handleClose={onClose}
			handleSave={() => confirm.run(onConfirm)}
			isLoading={isConfirmLoading || confirm.isRunning}
			isOpened={isOpened}
			size="xs"
			submitButtonColor={ButtonColor.DANGER}
			submitButtonText={ts("addToBlacklist")}
			title={ts("addToBlacklistTitle")}
		>
			<div className="text-sm text-gray-700">
				{ts("addToBlacklistConfirmation").replace("{{count}}", String(selectedCount))}
			</div>

			{isImpactLoading && (
				<div className="mt-3 flex items-center gap-2 text-ssm text-gray-500">
					<Spinner />
					<span>{ts("blacklistImpactChecking")}</span>
				</div>
			)}

			{impact &&
				!isImpactLoading &&
				(impact.onListCount > 0 || impact.inActiveCampaignCount > 0) && (
					<div className="mt-3 rounded-lg bg-gray-50 p-3 text-ssm">
						{impact.onListCount > 0 && (
							<div className="text-gray-700">
								{ts("blacklistImpactOnList", { count: impact.onListCount })}
							</div>
						)}
						{impact.inActiveCampaignCount > 0 && (
							<div className="mt-1 font-semibold text-status-warning">
								{ts("blacklistImpactActiveCampaign", { count: impact.inActiveCampaignCount })}
							</div>
						)}
						{impact.affectedListNames.length > 0 && (
							<div className="mt-1 text-gray-500">
								{ts("blacklistImpactLists", { names: impact.affectedListNames.join(", ") })}
							</div>
						)}
					</div>
				)}

			<CompanyBlacklistToggle
				isChecked={addEntireCompany}
				selection={companySelectionOf(impact, isImpactLoading, selectedCount)}
				onChange={onToggleEntireCompany}
			/>
		</Modal>
	);
};
